import React from 'react';
import { InputHelperText, useLocaleState, required as requiredValidate, useInput, TextInputProps } from 'react-admin';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled, Box } from '@mui/system';
import { Grid } from '@mui/material';
import { format } from 'date-fns';
import * as DateFnsLocales from 'date-fns/locale';
import type { Locale } from 'date-fns';
import cn from 'classnames';

import { FORMAT_DATE_STRING, LocaleMapping } from 'src/constants';
import { Theme } from 'src/Theme';

type LabelPosition = 'top' | 'left';

interface ShsDatePickerProps extends TextInputProps {
    label?: string;
    required?: boolean;
    labelPosition?: LabelPosition;
    removePaddingTop?: boolean;
    maxDate?: Date;
    minDate?: Date;
}

const styles = ({ labelPosition }: { labelPosition?: LabelPosition }) => ({
    '.MuiOutlinedInput-root': {
        '&:hover fieldset': {
            borderColor: `${Theme.palette.primary.main}`
        },
        '&:focus fieldset': {
            border: `3px solid #75CACB`
        }
    },
    // target the inner TextField root when it has `invalid` class
    '&.MuiFormControl-root.invalid .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d32f2f'
    },
    '& .MuiFormControl-root.invalid .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2FA9A6 !important'
    },
    '& legend': {
        display: 'none'
    },
    '& input.MuiInputBase-input': {
        background: '#ffffff',
        padding: '11px 16px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
        top: 0
    },
    '& .error': {
        color: '#d32f2f',
        fontSize: '0.75rem',
        padding: 0
    }
});

const StyledDatePicker = styled(DatePicker)(styles);

const StyledErrors = styled('p')({
    color: '#d32f2f',
    fontSize: '0.75rem',
    padding: 0,
    margin: '4px 2px',
    minHeight: '12px'
});

const ShsDatePicker: React.FC<ShsDatePickerProps> = ({
    source,
    label,
    required,
    labelPosition,
    disabled,
    removePaddingTop = false,
    maxDate,
    minDate
}) => {
    const [value, setValue] = React.useState<Date | null>(null);

    const [locale] = useLocaleState();
    const currentLocale = LocaleMapping[locale] || locale || 'enUS';
    const locales = DateFnsLocales as Record<string, Locale>;
    const adapterLocale = locales[currentLocale];

    const isRequired = () => (required ? '*' : '');
    const validateFn = [...(required ? [requiredValidate()] : [])];

    const {
        field,
        fieldState: { isTouched, invalid, error },
        formState: { isSubmitted }
    } = useInput({ source, isRequired: required, validate: validateFn });

    React.useEffect(() => {
        if (!field.value) {
            setValue(null);
            return;
        }

        let dateValue: Date;
        if (field.value instanceof Date) {
            dateValue = field.value;
        } else if (typeof field.value === 'string') {
            // If it's a date-only string YYYY-MM-DD, parse as local date
            if (/^\d{4}-\d{2}-\d{2}$/.test(field.value)) {
                const [year, month, day] = field.value.split('-').map(Number);
                dateValue = new Date(year, month - 1, day);
            } else {
                dateValue = new Date(field.value);
            }
        } else {
            dateValue = new Date(field.value);
        }

        setValue(isNaN(dateValue.getTime()) ? null : dateValue);
    }, [field.value]);

    const handleChange = (newValue: Date | null) => {
        // isNaN for the DateTime means that the Date in invalid
        if (newValue && isNaN(newValue.getTime())) {
            newValue = null;
        }
        setValue(newValue);
        if (newValue === null) {
            // Clearing value should immediately re-validate required
            field.onChange(null);
            field.onBlur();
            return;
        }
        field.onChange(format(newValue as Date, FORMAT_DATE_STRING));
        field.onBlur();
    };

    const renderHelperText = (isTouched || isSubmitted) && invalid;

    // Ensure Cyrillic placeholders are used while editing
    const cyrillicLocaleText = {
        fieldDayPlaceholder: () => 'ДД',
        fieldMonthPlaceholder: () => 'ММ',
        fieldYearPlaceholder: ({ digitAmount }: { digitAmount: number }) => 'Р'.repeat(digitAmount)
    };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={adapterLocale}
            localeText={cyrillicLocaleText}>
            <Grid
                container
                flexDirection={labelPosition === 'top' ? 'column' : 'row'}
                gap={labelPosition === 'top' ? '10px' : 0}
                sx={{ paddingTop: labelPosition === 'top' || removePaddingTop ? 0 : '16px' }}>
                {label && (
                    <Grid item sm={labelPosition === 'top' ? 12 : 6} alignItems="center" display="flex" height="45px">
                        <label className="text_field__label">
                            {label || ''}
                            {isRequired()}
                        </label>
                    </Grid>
                )}
                <Grid item sm={labelPosition === 'top' ? 12 : label ? 6 : 12}>
                    <Box
                        flexDirection="column"
                        className={cn('text_field__container', labelPosition ? labelPosition : 'top')}>
                        <StyledDatePicker
                            value={value}
                            onChange={handleChange}
                            onAccept={() => field.onBlur()}
                            onClose={() => field.onBlur()}
                            slotProps={{
                                textField: {
                                    placeholder: 'ДД.ММ.РРРР',
                                    className: invalid ? 'invalid' : '',
                                    onBlur: field.onBlur
                                }
                            }}
                            disabled={disabled}
                            maxDate={maxDate}
                            minDate={minDate}
                        />
                        {renderHelperText ? (
                            <StyledErrors>
                                <InputHelperText touched={isTouched || isSubmitted} error={error?.message} />
                            </StyledErrors>
                        ) : null}
                    </Box>
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};

export default ShsDatePicker;
